import {BillingUserType, UserType} from "data/types";

// ── Card ──

export type WompiCardBrand = "visa" | "mastercard" | "amex";

export const CARD_RULES: Record<
  WompiCardBrand,
  {length: number; cvcLength: number; groups: number[]}
> = {
  visa: {length: 16, cvcLength: 3, groups: [4, 4, 4, 4]},
  mastercard: {length: 16, cvcLength: 3, groups: [4, 4, 4, 4]},
  amex: {length: 15, cvcLength: 4, groups: [4, 6, 5]},
};

export const cardBrandOf = (digits: string): WompiCardBrand | null => {
  if (digits.startsWith("4")) return "visa";
  if (/^3[47]/.test(digits)) return "amex";
  const two = Number(digits.slice(0, 2)) || 0;
  const four = Number(digits.slice(0, 4)) || 0;
  if ((two >= 51 && two <= 55) || (four >= 2221 && four <= 2720)) {
    return "mastercard";
  }
  return null;
};

export const isLuhnValid = (digits: string) => {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let n = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
};

/** "4242424242424242" → "4242 4242 4242 4242", grouped as the brand prints it. */
export const formatCardNumber = (value: string) => {
  const brand = cardBrandOf(value.replace(/\D/g, ""));
  const groups = brand ? CARD_RULES[brand].groups : [4, 4, 4, 4];
  const max = groups.reduce((a, b) => a + b, 0);
  const digits = value.replace(/\D/g, "").slice(0, max);
  const parts: string[] = [];
  let start = 0;
  for (const size of groups) {
    if (start >= digits.length) break;
    parts.push(digits.slice(start, start + size));
    start += size;
  }
  return parts.join(" ");
};

/** "1228" → "12/28". */
export const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2
    ? `${digits.slice(0, 2)}/${digits.slice(2)}`
    : digits;
};

/** Valid through the last day of its month. */
export const isExpiryValid = (expiry: string) => {
  const [month, year] = expiry.split("/").map(Number);
  if (!month || year === undefined || expiry.length !== 5) return false;
  if (month < 1 || month > 12) return false;
  return new Date(2000 + year, month, 1) > new Date();
};

// ── Transaction ──

export type WompiStatus =
  "PENDING" | "APPROVED" | "DECLINED" | "VOIDED" | "ERROR" | "UNKNOWN";

export interface WompiTransaction {
  id: string;
  reference: string;
  status: WompiStatus;
  statusMessage: string;
  hasThreeDs: boolean;
  /** The bank's page, when it waits for the customer to confirm. */
  challenge: string | null;
}

const STATUSES: WompiStatus[] = [
  "PENDING",
  "APPROVED",
  "DECLINED",
  "VOIDED",
  "ERROR",
];

export const isSettled = (status: WompiStatus) => status !== "PENDING";

const unescapeHtml = (html: string) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
};

export const parseTransaction = (json: any): WompiTransaction => {
  const threeDs = json?.payment_method?.extra?.three_ds_auth;
  const status = `${json?.status ?? ""}`.toUpperCase() as WompiStatus;
  const page = `${threeDs?.three_ds_method_data ?? ""}`;
  const waitingForCustomer =
    threeDs?.current_step === "CHALLENGE" &&
    threeDs?.current_step_status === "PENDING";
  return {
    id: `${json?.id ?? ""}`,
    reference: `${json?.reference ?? ""}`,
    status: STATUSES.includes(status) ? status : "UNKNOWN",
    statusMessage: `${json?.status_message ?? ""}`.trim(),
    hasThreeDs: !!threeDs,
    challenge: waitingForCustomer && page ? unescapeHtml(page) : null,
  };
};

/**
 * The server's `message` is text, or Wompi's detail as JSON text
 * (`{"reference":["..."]}`), which reads as "reference: ...".
 */
export const reasonOf = (body: any): string => {
  const message = body?.message;
  if (Array.isArray(message)) return message.join(", ");
  const text = `${message ?? ""}`;
  try {
    const detail = JSON.parse(text);
    if (detail && typeof detail === "object") {
      return Object.entries(detail)
        .map(
          ([key, value]) =>
            `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
        )
        .join("; ");
    }
  } catch (_) {}
  return text;
};

/** The status of a failed RTK call; 0 when there was no answer. */
export const statusOf = (error: any): number =>
  typeof error?.status === "number" ? error.status : 0;

// ── Customer ──

export interface WompiCustomer {
  email: string;
  fullName?: string;
  phoneNumberPrefix?: string;
  phoneNumber?: string;
  legalId?: string;
  legalIdType?: string;
}

const legalIdTypeOf = (type: string) => {
  switch (type.trim().toUpperCase()) {
    case "CI":
    case "CC":
    case "CEDULA":
    case "CÉDULA":
      return "CC";
    case "CE":
      return "CE";
    case "PASSPORT":
    case "PASAPORTE":
    case "PP":
      return "PP";
    case "RUC":
    case "NIT":
      return "NIT";
    case "DNI":
      return "DNI";
    case "TI":
      return "TI";
    default:
      return "";
  }
};

/**
 * Who pays: the billing details picked at checkout first, the profile for
 * whatever they lack. Only complete pairs (prefix and phone, id and its type)
 * are sent.
 */
export const wompiCustomerOf = (
  billing: BillingUserType | null,
  user: UserType | null,
): WompiCustomer => {
  const firstOf = (preferred?: string, fallback?: string) =>
    preferred?.trim() ? preferred.trim() : `${fallback ?? ""}`.trim();

  const prefix = firstOf(billing?.prefix, user?.prefix);
  const phonePrefix = !prefix || prefix.startsWith("+") ? prefix : `+${prefix}`;
  const phone = firstOf(billing?.phone, user?.phone).replace(/\D/g, "");
  const legalId = firstOf(billing?.identification, user?.ci);
  const legalIdType = legalIdTypeOf(
    firstOf(billing?.documentType, user?.document),
  );
  const fullName = firstOf(billing?.name, user?.name);

  return {
    email: firstOf(billing?.email, user?.email),
    ...(fullName && {fullName}),
    ...(phonePrefix &&
      phone && {phoneNumberPrefix: phonePrefix, phoneNumber: phone}),
    ...(legalId && legalIdType && {legalId, legalIdType}),
  };
};

/** What 3D Secure needs to know about the browser paying. */
export const browserInfo = () => ({
  browser_color_depth: `${window.screen.colorDepth}`,
  browser_screen_height: `${window.screen.height}`,
  browser_screen_width: `${window.screen.width}`,
  browser_language: navigator.language,
  browser_user_agent: navigator.userAgent,
  browser_tz: `${new Date().getTimezoneOffset()}`,
});
